import { Loader2, MailIcon, PhoneIcon, User2Icon } from "lucide-react";
import { useCallback, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import type { TGoogleSignInSchema } from "@/api/auth";
import type { GoogleOAuthTokens } from "@/hooks/use-google-auth";
import type { FormSchema } from "@/lib/schema";

import { authenticateUser, googleSign } from "@/api/auth";
import { useCountries } from "@/api/util";
import {
	CustomCheckbox,
	CustomFormField,
	PasswordFields
} from "@/components/form/custom-formfields";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { useAuthenticateUser } from "@/hooks/use-authenticate";
import { useGoogleOAuthSignIn } from "@/hooks/use-google-auth";
import { AuthFormSchema } from "@/lib/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import {
	createFileRoute,
	Link,
	redirect,
	useNavigate
} from "@tanstack/react-router";

export const Route = createFileRoute("/_auth/sign-up")({
	component: AuthForm,
	validateSearch: z
		.object({
			state: z.enum(["BUYER", "VENDOR"]).optional(),
			userType: z.enum(["BUYER", "VENDOR"]).optional()
		})
		.transform(data => ({
			...data,
			userType: data.userType ?? data.state
		})),
	onError: (error) => {
		if (error.routerCode === "VALIDATE_SEARCH") {
			toast.error("Invalid User Type");
			throw redirect({ to: "/user-type" });
		}
	}
});

const FORM_CONFIGS = {
	"sign-up": {
		title: "Welcome to Intasida",
		subtitle: "Create an account to get started",
		buttonText: "Sign Up",
		altText: "Already have an account?",
		altLink: "/sign-in",
		altLinkText: "Sign in"
	}
} as const;

function AuthForm() {
	const navigate = useNavigate();
	const { userType } = Route.useSearch();

	const formConfig = FORM_CONFIGS["sign-up"];

	const { data: countries } = useCountries();

	const googleSignIn = useGoogleOAuthSignIn();
	const authMutation = useAuthenticateUser((data, type) =>
		authenticateUser(data as FormSchema, type)
	);

	const country = countries?.data.find(country => country.name === "Nigeria");

	if (country)
		localStorage.setItem("country", country.id);

	const countryId = country ? country.id : localStorage.getItem("country");
	const googleMutation = useAuthenticateUser(data =>
		googleSign({ ...data, userType, roles: userType, countryId, language: "English" } as TGoogleSignInSchema)
	);

	const form = useForm<FormSchema>({
		resolver: zodResolver(AuthFormSchema("sign-up")),
		defaultValues: {
			email: "",
			password: "",
			tos: false,
			firstName: "",
			lastName: "",
			username: "",
			phoneNumber: "",
			referrerCode: "",
			confirmPassword: ""
		}
	});

	const handleGoogleSignIn = useCallback(async (response: GoogleOAuthTokens) => {
		const data = { token: response.id_token };

		try {
			await googleMutation.mutateAsync({
				data,
				type: "sign-up"
			});
		}
		catch (error) {
			console.error("Error during Google sign-in:", error);
		}
	}, [googleMutation]);

	const handleSubmit = async (values: FormSchema) => {
		if (!userType)
			throw new Error("User type is required");
		try {
			await authMutation.mutateAsync({
				data: { ...values, userType },
				type: "sign-up"
			});
		}
		catch (error) {
			console.error("Authentication error:", error);
		}
	};

	const googleHandledRef = useRef(false);

	useEffect(() => {
		const handleAuthSuccess = () => {
			if (authMutation.isSuccess) {
				navigate({
					to: "/verify-email",
					state: {
						verifyEmailOpts: {
							email: authMutation.data?.data.user.email,
							hash: authMutation.data?.data.hash
						}
					}
				});
			}
			else {
				navigate({ to: `/${userType?.toLowerCase()}/home` });
			}
		};

		if (authMutation.isSuccess || googleMutation.isSuccess)
			handleAuthSuccess();
		// Only trigger Google backend auth once when token exchange succeeds
		if (googleSignIn.isSuccess && !googleHandledRef.current) {
			googleHandledRef.current = true;
			handleGoogleSignIn(googleSignIn.data);
		}
		// Reset on Google errors so user can retry with a different account
		if (googleSignIn.isError) {
			googleHandledRef.current = false;
		}
		if (googleMutation.isError) {
			googleHandledRef.current = false;
		}
	}, [
		userType,
		googleMutation.isSuccess,
		googleMutation.isError,
		authMutation.isSuccess,
		googleSignIn.isSuccess,
		googleSignIn.isError,
		googleSignIn.data,
		handleGoogleSignIn
	]);

	return (
		<>
			<div className="mb-4">
				<button
					type="button"
					aria-label="Go back"
					className="rounded-full p-2 transition-colors duration-150 hover:bg-gray-200"
					onClick={() => window.history.back()}
				>
					<img
						src="/assets/icons/arrow-left.svg"
						alt="Back"
						style={{ width: 24, height: 24, display: "block" }}
					/>
				</button>
			</div>
			<section className="mx-auto w-full max-w-md">
				<AuthHeader
					title={formConfig?.title}
					subtitle={formConfig?.subtitle}
					onGoogleSignIn={() => googleSignIn.signIn(userType)}
				/>

			<Form {...form}>
				<form
					onSubmit={form.handleSubmit(handleSubmit)}
					className="space-y-6"
				>
					<ErrorMessage error={authMutation.error || googleMutation.error || googleSignIn.error} />

					<SignUpFields control={form.control} />

					<CustomFormField
						control={form.control}
						placeholder="Enter your email"
						className="h-14"
						type="email"
						Icon={MailIcon}
						name="email"
					/>

					<PasswordFields
						control={form.control}
						showConfirm
					/>
					<CustomCheckbox
						name="tos"
						control={form.control}
						label="I agree to the Terms of Service"
					/>

					<Button disabled={authMutation.isPending || googleMutation.isPending} type="submit" className="h-14 w-full">
						<LoadingButton isLoading={authMutation.isPending || googleMutation.isPending} text={formConfig?.buttonText} />
					</Button>

				</form>
			</Form>
			<div className="my-4 flex justify-center gap-1 max-sm:justify-start">
				<p className="text-14 max-sm:text-9 font-normal text-gray-600">
					{formConfig?.altText}
				</p>
				<Link
					to={formConfig?.altLink}
					className="font-medium text-primary"
				>
					{formConfig?.altLinkText}
				</Link>
			</div>
			</section>
		</>
	);
}

function LoadingButton({
	isLoading,
	text
}: {
	isLoading: boolean;
	text: string;
}) {
	return (
		<>
			{isLoading
				? (
						<>
							<Loader2 size={20} className="animate-spin" />
							{" "}
							Loading....
						</>
					)
				: (
						text
					)}
		</>
	);
}

function AuthHeader({
	title,
	subtitle,
	onGoogleSignIn
}: {
	title: string;
	subtitle: string;
	onGoogleSignIn: () => void;
}) {
	return (
		<>
			<div className="my-4">
				<h1 className="mb-12 h-1 text-3xl font-semibold leading-9 max-sm:text-2xl">
					{title}
				</h1>
				<p className="text-16 font-light leading-4">{subtitle}</p>
			</div>
			<Button
				onClick={onGoogleSignIn}
				variant="secondary"
				className="group mt-1 h-14 w-full gap-6 hover:bg-primary"
			>
				<img src="/google.svg" width={20} height={20} alt="google" />
				<span className="group-hover:text-white">Sign up with Google</span>
			</Button>
			<div className="my-8 flex items-center">
				<hr className="grow border-t border-gray-300" />
				<span className="mx-2 text-gray-400">Or Sign up with Email</span>
				<hr className="grow border-t border-gray-300" />
			</div>
		</>
	);
}

function ErrorMessage({ error }: { error: Error | null }) {
	if (!error)
		return null;

	return (
		<p className="text-center text-destructive">
			{error.stack?.includes("401")
				? error.message
				: "An error occurred. Please try again."}
		</p>
	);
}

function SignUpFields({ control }: { control: any }) {
	return (
		<div className="flex flex-col gap-6">
			{(["firstName", "lastName", "username"] as const).map(field => (
				<CustomFormField
					key={field}
					control={control}
					placeholder={`Enter your ${
						field.charAt(0).toUpperCase() + field.slice(1).toLowerCase()
					}`}
					className="h-14"
					type="text"
					Icon={User2Icon}
					name={field}
				/>
			))}
			<CustomFormField
				control={control}
				placeholder="Enter your phone number"
				className="h-14"
				type="tel"
				Icon={PhoneIcon}
				name="phoneNumber"
			/>
			<CustomFormField
				control={control}
				placeholder="Enter referral code (optional)"
				className="h-14"
				type="text"
				Icon={User2Icon}
				name="referrerCode"
			/>
		</div>
	);
}
